//frontend/src/components/Dossier/Preferences/StudyVisaPreferences.jsx

import React, { useState, useEffect, useCallback } from 'react';
import { Container, Card, Form, Button, Row, Col, Alert } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { FaGraduationCap, FaUniversity, FaMapMarkerAlt, FaCalendar, FaDollarSign, FaEye } from 'react-icons/fa';
import api from '../../../services/api';
import { toast } from 'react-toastify';

const StudyVisaPreferences = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [viewOnly, setViewOnly] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    
    const [formData, setFormData] = useState({
        study_level: '',
        field_of_study: '',
        preferred_university: '',
        preferred_city: '',
        start_date: '',
        duration_months: '',
        has_acceptance_letter: false,
        funding_source: '',
    });

    const studyLevels = [
        { value: 'high_school', label: 'Lycée (High School)' },
        { value: 'college', label: 'Collège (College/CEGEP)' },
        { value: 'bachelor', label: 'Licence/Bachelor' },
        { value: 'master', label: 'Master/Maîtrise' },
        { value: 'doctorate', label: 'Doctorat (PhD)' },
        { value: 'language', label: 'Cours de langue' },
    ];

    const cities = [
        'Toronto', 'Montréal', 'Vancouver', 'Calgary', 'Ottawa', 
        'Edmonton', 'Winnipeg', 'Québec', 'Hamilton', 'London', 'Autre'
    ];

    const loadExistingPreferences = useCallback(async () => {
        try {
            // Charger le dossier pour vérifier le statut
            const dossierResponse = await api.get(`/dossiers/${id}/`);
            const isDraft = dossierResponse.data.status === 'draft';
            setViewOnly(!isDraft);

            // Charger les préférences existantes
            try {
                const preferencesResponse = await api.get(`/dossiers/${id}/study-preferences/`);
                setFormData(preferencesResponse.data.preferences);
                setIsEditing(true);
            } catch (error) {
                // 404 signifie qu'aucune préférence n'existe encore
                if (error.response?.status === 404) {
                    // C'est normal, on continue en mode création
                    setIsEditing(false);
                    if (!isDraft) {
                        toast.info('Aucune préférence enregistrée pour ce dossier');
                        setTimeout(() => navigate(`/dossier/${id}`), 2000);
                    }
                } else {
                    // Autre erreur
                    console.error('Error loading preferences:', error);
                    toast.error('Erreur lors du chargement');
                }
            }
        } catch (error) {
            console.error('Error loading dossier:', error);
            toast.error('Erreur lors du chargement du dossier');
        }
    }, [id, navigate]);

    useEffect(() => {
        loadExistingPreferences();
    }, [loadExistingPreferences]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData({
            ...formData,
            [name]: type === 'checkbox' ? checked : value,
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const method = isEditing ? 'patch' : 'post';
            await api[method](`/dossiers/${id}/study-preferences/`, formData);
            
            toast.success(isEditing ? '✅ Préférences mises à jour !' : '✅ Préférences enregistrées !');
            setTimeout(() => navigate(`/dossier/${id}`), 1000);
        } catch (error) {
            toast.error('❌ Erreur lors de l\'enregistrement');
            console.error('Error saving preferences:', error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Container className="py-4">
            <Row className="mb-4">
                <Col>
                    <h2>
                        <FaGraduationCap className="me-3" />
                        {viewOnly ? 'Préférences - Visa d\'Études' : 
                         isEditing ? 'Modifier les préférences - Visa d\'Études' : 
                         'Préférences - Visa d\'Études'}
                    </h2>
                    <p className="text-muted">
                        {viewOnly ? 'Consultation de vos préférences enregistrées' : 
                         'Renseignez vos préférences pour vos études au Canada'}
                    </p>
                </Col>
            </Row>

            {viewOnly && (
                <Alert variant="info" className="mb-4">
                    <FaEye className="me-2" />
                    Ce dossier a été soumis. Vous ne pouvez plus modifier vos préférences.
                </Alert>
            )}

            <Form onSubmit={handleSubmit}>
                {/* Programme d'études */}
                <Card className="mb-4">
                    <Card.Header>
                        <FaGraduationCap className="me-2" />
                        Programme d'études
                    </Card.Header>
                    <Card.Body>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Niveau d'études *</Form.Label>
                                    <Form.Select
                                        name="study_level"
                                        value={formData.study_level}
                                        onChange={handleChange}
                                        required
                                        disabled={viewOnly}
                                    >
                                        <option value="">Sélectionner un niveau</option>
                                        {studyLevels.map(level => (
                                            <option key={level.value} value={level.value}>
                                                {level.label}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Domaine d'études *</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="field_of_study"
                                        value={formData.field_of_study}
                                        onChange={handleChange}
                                        required
                                        disabled={viewOnly}
                                        placeholder="Ex: Informatique, Médecine, Commerce..."
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <Form.Group className="mb-3">
                            <Form.Label>
                                <FaUniversity className="me-1" />
                                Université/Établissement préféré
                            </Form.Label>
                            <Form.Control
                                type="text"
                                name="preferred_university"
                                value={formData.preferred_university}
                                onChange={handleChange}
                                disabled={viewOnly}
                                placeholder="Ex: Université de Toronto, McGill, UBC..."
                            />
                            {!viewOnly && (
                                <Form.Text className="text-muted">
                                    Optionnel - Si vous avez une préférence
                                </Form.Text>
                            )}
                        </Form.Group>
                    </Card.Body>
                </Card>

                {/* Localisation et dates */}
                <Card className="mb-4">
                    <Card.Header>
                        <FaMapMarkerAlt className="me-2" />
                        Localisation et dates
                    </Card.Header>
                    <Card.Body>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Ville préférée *</Form.Label>
                                    <Form.Select
                                        name="preferred_city"
                                        value={formData.preferred_city}
                                        onChange={handleChange}
                                        required
                                        disabled={viewOnly}
                                    >
                                        <option value="">Sélectionner une ville</option>
                                        {cities.map(city => (
                                            <option key={city} value={city.toLowerCase()}>
                                                {city}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>
                                        <FaCalendar className="me-1" />
                                        Date de début souhaitée *
                                    </Form.Label>
                                    <Form.Control
                                        type="date"
                                        name="start_date"
                                        value={formData.start_date}
                                        onChange={handleChange}
                                        required
                                        disabled={viewOnly}
                                        min={new Date().toISOString().split('T')[0]}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <Form.Group className="mb-3">
                            <Form.Label>Durée prévue des études (en mois) *</Form.Label>
                            <Form.Control
                                type="number"
                                name="duration_months"
                                value={formData.duration_months}
                                onChange={handleChange}
                                required
                                disabled={viewOnly}
                                min="1"
                                max="120"
                                placeholder="Ex: 24 pour 2 ans"
                            />
                        </Form.Group>
                    </Card.Body>
                </Card>

                {/* Financement */}
                <Card className="mb-4">
                    <Card.Header>
                        <FaDollarSign className="me-2" />
                        Financement
                    </Card.Header>
                    <Card.Body>
                        <Form.Group className="mb-3">
                            <Form.Check
                                type="checkbox"
                                name="has_acceptance_letter"
                                checked={formData.has_acceptance_letter}
                                onChange={handleChange}
                                label="J'ai déjà reçu une lettre d'acceptation"
                                disabled={viewOnly}
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Source de financement *</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                name="funding_source"
                                value={formData.funding_source}
                                onChange={handleChange}
                                required
                                disabled={viewOnly}
                                placeholder="Ex: Bourse d'études, Fonds personnels, Soutien familial, Prêt étudiant..."
                            />
                            {!viewOnly && (
                                <Form.Text className="text-muted">
                                    Décrivez comment vous comptez financer vos études
                                </Form.Text>
                            )}
                        </Form.Group>
                    </Card.Body>
                </Card>

                {!viewOnly && (
                    <Alert variant="info" className="mb-4">
                        <i className="fas fa-info-circle me-2"></i>
                        <strong>Important :</strong> Vous devrez fournir une preuve de capacité financière pour couvrir vos frais de scolarité et de subsistance au Canada.
                    </Alert>
                )}

                <div className="d-flex justify-content-between">
                    <Button 
                        variant="secondary" 
                        onClick={() => navigate(`/dossier/${id}`)}
                    >
                        ← Retour
                    </Button>
                    
                    {!viewOnly && (
                        <Button 
                            type="submit" 
                            variant="primary" 
                            disabled={loading}
                        >
                            {loading ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2"></span>
                                    Enregistrement...
                                </>
                            ) : (
                                isEditing ? 'Mettre à jour les préférences' : 'Enregistrer les préférences'
                            )}
                        </Button>
                    )}
                </div>
            </Form>
        </Container>
    );
};

export default StudyVisaPreferences;